import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  Images,
  Upload,
  History,
  Trash2,
  X,
  Link as LinkIcon,
  Film,
  ImageIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { useAccount } from '../../context/AccountContext';
import {
  GET_MEDIA_ASSETS,
  GET_MEDIA_RENDITIONS,
  RESTORE_MEDIA_RENDITION,
  DELETE_MEDIA_ASSET,
} from '../../graphql';
import { sha256Hex, uploadAsset } from '../../lib/media/uploadAsset';
import { apiFetch } from '../../lib/apiFetch';
import {
  formatDuration,
  isVideoFile,
  uploadVideo,
  waitForVideo,
  type VideoInfo,
} from '../../lib/media/uploadVideo';
import { MediaAllowanceBar, useMediaAllowance } from '../media/MediaAllowance';
import { VideoCoverDialog } from '../media/VideoCoverDialog';
import { LinkedMedia, useLinkedMedia } from '../media/LinkedMedia';
import { Loader } from '../common/PageLoader';
import { askInk } from '../cms/InkConfirm';
import { showInkToast } from '../cms/InkToast';

/**
 * The media library — the browser surface for the platform's asset layer.
 * Not yet extended: the Ink review only places Media next to Articles. This
 * grid, dedupe, and version history stay as they are. See src/styles/INK.md.
 *
 * Two things here are not the obvious implementation, and both are deliberate:
 *
 * 1. The browser hashes each file BEFORE uploading and sends only the digest.
 *    If the account already holds those bytes the server answers `deduped` and
 *    returns no upload URL, so re-adding a scan you already have costs one
 *    small request instead of several megabytes.
 *
 * 2. Bytes go straight to blob storage via a short-lived, write-only URL. They
 *    never pass through the API server, which is what keeps a burst of large
 *    originals from turning the API into a bottleneck.
 */

const PAGE_SIZE = 48;

interface Rendition {
  id: string;
  kind: string;
  url: string | null;
  width: number | null;
  height: number | null;
  bytes?: number | null;
  isCurrent: boolean;
  supersededAt?: string | null;
  createdAt: string;
  transform?: Record<string, unknown> | null;
}

interface MediaUsage {
  ownerType: string;
  ownerId: string;
  role: string;
  position: number;
  title: string | null;
  status: string | null;
}

interface MediaAsset {
  id: string;
  sha256: string;
  mime: string | null;
  bytes: number | null;
  width: number | null;
  height: number | null;
  originalFilename: string | null;
  captureMeta?: Record<string, unknown> | null;
  createdAt: string;
  renditions: Rendition[];
  usage: MediaUsage[];
  usageCount: number;
  video?: VideoInfo | null;
}

const VIDEO_STATUS: Record<string, string> = {
  uploading: 'uploading',
  processing: 'processing',
  error: 'failed',
  rejected: 'too long for your plan',
};

/** A video tile: its poster with the length, or where it is in processing. */
function VideoThumb({ asset, className }: { asset: MediaAsset; className?: string }) {
  const v = asset.video!;
  return (
    <>
      {v.posterUrl ? (
        <img
          src={v.posterUrl}
          alt={asset.originalFilename || 'video'}
          loading="lazy"
          className={className}
        />
      ) : (
        <Film className="w-6 h-6 opacity-40" />
      )}
      <span
        style={{
          position: 'absolute',
          left: 6,
          bottom: 6,
          padding: '1px 6px',
          borderRadius: 6,
          fontSize: 11,
          background: 'rgba(0,0,0,0.65)',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Film className="w-3 h-3" />
        {v.status === 'ready'
          ? formatDuration(v.durationSeconds)
          : VIDEO_STATUS[v.status] || v.status}
      </span>
    </>
  );
}

const isFont = (asset: MediaAsset) => (asset.mime || '').startsWith('font/');
const isAudio = (asset: MediaAsset) => (asset.mime || '').startsWith('audio/');
const isModel = (asset: MediaAsset) => (asset.mime || '').startsWith('model/');
/** A 3D model is picked as a set: the .gltf with the files it uses, or one .glb. */
const isModelSelection = (files: File[]) => files.some((f) => /\.(gltf|glb)$/i.test(f.name));
const ACCEPT = 'image/*,video/*,audio/*,.woff2,.woff,.ttf,.otf,.gltf,.glb,.bin,.ktx2';

/** A sound file's tile: the browser's player, so it can be heard here. Pages play it in an audio block. */
function AudioThumb({ asset, url }: { asset: MediaAsset; url: string | null | undefined }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 w-full h-full px-2"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <span className="text-[11px] opacity-60">audio</span>
      {url ? (
        <audio
          controls
          preload="none"
          src={url}
          style={{ width: '100%' }}
          aria-label={asset.originalFilename || 'audio'}
        />
      ) : (
        <span className="text-[11px] opacity-60">Not arrived yet</span>
      )}
    </div>
  );
}

/**
 * A font tile: a sample drawn in the font itself, and its link to copy into
 * the site's custom CSS (@font-face). Pages never show a font as an image.
 */
function FontThumb({ asset, url }: { asset: MediaAsset; url: string | null | undefined }) {
  const family = `media-${asset.id}`;
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!url || typeof FontFace === 'undefined') return;
    let live = true;
    new FontFace(family, `url(${JSON.stringify(url)})`)
      .load()
      .then((face) => {
        document.fonts.add(face);
        if (live) setLoaded(true);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [family, url]);
  return (
    <div className="flex flex-col items-center justify-center gap-2 w-full h-full">
      <span style={{ fontFamily: loaded ? family : undefined, fontSize: 34, lineHeight: 1 }}>
        Aa
      </span>
      <span className="text-[11px] opacity-60">font</span>
      {url && (
        <button
          type="button"
          className="text-[11px] underline"
          title="For @font-face in your site's custom CSS"
          onClick={(e) => {
            e.stopPropagation();
            void navigator.clipboard?.writeText(url).then(() => setCopied(true));
          }}
        >
          {copied ? 'Link copied' : 'Copy link'}
        </button>
      )}
    </div>
  );
}

/**
 * A 3D model tile: what it is, and its link for a page's own code to load (its
 * textures and buffers sit beside it, so a glTF loader finds them).
 */
function ModelThumb({ asset, url }: { asset: MediaAsset; url: string | null | undefined }) {
  const [copied, setCopied] = useState(false);
  const glb = (asset.mime || '') === 'model/gltf-binary';
  return (
    <div className="flex flex-col items-center justify-center gap-2 w-full h-full px-2 text-center">
      <span style={{ fontSize: 30, lineHeight: 1 }} aria-hidden>
        ◈
      </span>
      <span className="text-[11px] opacity-60">3D model · {glb ? 'glb' : 'gltf'}</span>
      {url ? (
        <button
          type="button"
          className="text-[11px] underline"
          title="For your page code: load this with a glTF loader"
          onClick={(e) => {
            e.stopPropagation();
            void navigator.clipboard?.writeText(url).then(() => setCopied(true));
          }}
        >
          {copied ? 'Link copied' : 'Copy link'}
        </button>
      ) : null}
    </div>
  );
}

/** Prefer the current canonical rendition; fall back to the original. */
function displayRendition(asset: MediaAsset): Rendition | null {
  const current = asset.renditions?.filter((r) => r.isCurrent) ?? [];
  return (
    current.find((r) => r.kind === 'thumb') ??
    current.find((r) => r.kind === 'web') ??
    current.find((r) => r.kind === 'canonical') ??
    current.find((r) => r.kind === 'original') ??
    null
  );
}

/** One-line "where is this used" summary, for the badge tooltip. */
function usageSummary(usage: MediaUsage[]): string {
  if (!usage?.length) return 'Not used anywhere';
  return usage
    .map(
      (u) => `${u.title || OWNER_LABEL[u.ownerType] || u.ownerType} (${u.role} · ${usageKind(u)})`
    )
    .join('\n');
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/**
 * Where a card says it's used, as a count (App Prototype, Media; backlog
 * 289d4408): "Used in 2 articles", "Used on 1 page", "Used in 3 places" for a
 * mix. The titles are in the versions dialog. `others` leaves out the site's
 * own images ("Used site-wide").
 */
function usedLabel(others: MediaUsage[]): string {
  const where = usedWhere(others);
  return `Used ${where.endsWith('page') || where.endsWith('pages') ? 'on' : 'in'} ${where}`;
}

/** "2 articles", "1 page", "3 places": what uses it, counted by owner. */
function usedWhere(others: MediaUsage[]): string {
  const owners = new Set(others.map((u) => `${u.ownerType}:${u.ownerId}`));
  const types = new Set(others.map((u) => u.ownerType));
  const [type] = Array.from(types);
  if (types.size === 1 && type === 'page') return plural(owners.size, 'page');
  if (types.size === 1 && type === 'topic') return plural(owners.size, 'article');
  if (types.size === 1 && type === 'social_post') return plural(owners.size, 'post');
  return plural(owners.size, 'place');
}

/**
 * Why it can't be deleted yet, in the server's terms (media/service.js
 * deleteAsset), or null. Site images need another chosen first; a page or an
 * article's text shows it by address, so it has to come off there first. A
 * linked use (lead image, a post) is detached by the delete instead.
 */
function deleteBlock(usage: MediaUsage[]): string | null {
  const site = siteUses(usage);
  if (site.length) {
    return `To delete it, choose another ${site.join(', ').toLowerCase()} in Website › Manage › Site first.`;
  }
  const shown = usage.filter(
    (u) => u.ownerType === 'page' || (u.ownerType === 'topic' && u.role === 'in the text')
  );
  if (!shown.length) return null;
  const names = Array.from(new Set(shown.map((u) => `“${u.title || 'Untitled'}”`)));
  return `To delete it, take it off ${names.slice(0, 3).join(', ')}${
    names.length > 3 ? ' and more' : ''
  } first.`;
}

/** The versions a person deals with: thumbnails are made for the grid, not edited. */
const editVersions = (renditions: Rendition[] = []) => renditions.filter((r) => r.kind !== 'thumb');

/** "Article · Live" for the Used by list (backlog c3ea532d). */
const OWNER_LABEL: Record<string, string> = {
  topic: 'Article',
  page: 'Page',
  site: 'Site',
  social_post: 'Post',
  form: 'Form',
};
const STATUS_LABEL: Record<string, string> = {
  published: 'Live',
  live: 'Live',
  draft: 'Draft',
  scheduled: 'Scheduled',
  archived: 'Archived',
};
function usageKind(u: MediaUsage): string {
  const owner = OWNER_LABEL[u.ownerType] || u.ownerType.replace(/_/g, ' ');
  const status = u.status ? STATUS_LABEL[u.status.toLowerCase()] || u.status : '';
  return status ? `${owner} · ${status}` : owner;
}

/** "Oct 21, 10:36 PM", with the year when it isn't this one (the app's date format). */
function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** What each kind of version is, in plain words. */
const KIND_LABEL: Record<string, string> = {
  original: 'Original',
  web: 'Web size',
  canonical: 'Edited',
  thumb: 'Thumbnail',
};

/**
 * The one version the site serves (backlog 944d5e83): the current web size,
 * else the current edit, else the original. Every kind has a "current" one,
 * so that flag alone can't say which readers see.
 */
function servedId(renditions: Rendition[]): string | null {
  const current = renditions.filter((r) => r.isCurrent);
  return (
    (
      current.find((r) => r.kind === 'web') ??
      current.find((r) => r.kind === 'canonical') ??
      current.find((r) => r.kind === 'original')
    )?.id ?? null
  );
}

/** Where the usage list links an owner, when the app has a screen for it. */
function usageHref(u: MediaUsage): string | null {
  if (u.ownerType === 'topic') return `/content/${u.ownerId}`;
  if (u.ownerType === 'page') return `/content?view=pages&page=${u.ownerId}`;
  if (u.ownerType === 'site') return '/content?view=manage&area=site';
  return null;
}

/** The site settings that use an asset, as chip labels: "Logo", "Cover", "Site icon". */
function siteUses(usage: MediaUsage[]): string[] {
  return (usage || []).filter((u) => u.ownerType === 'site').map((u) => u.title || u.role);
}

const versionsLabel = (n: number) => `${n} ${n === 1 ? 'version' : 'versions'}`;

function formatBytes(n: number | null | undefined): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

type UploadState = {
  name: string;
  status: 'hashing' | 'uploading' | 'done' | 'deduped' | 'error';
  message?: string;
};

export function MediaLibrary({
  embedded = false,
  onReady,
}: {
  embedded?: boolean;
  /** Opens the file picker. The Content header uses this for its Upload button. */
  onReady?: (openUpload: () => void) => void;
}) {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const client = useApolloClient();
  const { allowance, refetch: refetchAllowance } = useMediaAllowance(accountId);
  const [offset, setOffset] = useState(0);
  const [fileQuery, setFileQuery] = useState('');
  // The search the server answers, a moment after typing stops (backlog 33da36e4).
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(fileQuery.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(t);
  }, [fileQuery]);
  // Feed images articles show by address (never stored here): listed under the grid.
  const linked = useLinkedMedia(embedded ? accountId : null, search);
  const [usage, setUsage] = useState<'all' | 'used' | 'unused'>('all');
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [historyFor, setHistoryFor] = useState<MediaAsset | null>(null);
  const [coverFor, setCoverFor] = useState<MediaAsset | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    onReadyRef.current?.(() => fileInput.current?.click());
  }, []);

  // In use / Unused filter the whole library on the server, so a filter's
  // pages and its count are all of it, not the 48 in hand (backlog 70edc5fe).
  // Search too: the grid and the counts are the matches across the library.
  const { data, loading, error, refetch } = useQuery(GET_MEDIA_ASSETS, {
    variables: {
      accountId,
      limit: PAGE_SIZE,
      offset,
      usage: usage === 'all' ? null : usage,
      search: search || null,
    },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteAsset] = useMutation(DELETE_MEDIA_ASSET);

  const page = data?.mediaAssets;
  const assets: MediaAsset[] = useMemo(() => page?.assets ?? [], [page]);
  const shown = assets;
  const clearSearch = () => {
    setFileQuery('');
    setSearch('');
    setOffset(0);
  };
  const usedCount = page?.usedTotal ?? 0;
  const unusedCount = page?.unusedTotal ?? 0;
  const allCount = page ? (page.usedTotal ?? 0) + (page.unusedTotal ?? 0) : assets.length;

  const setUpload = (name: string, patch: Partial<UploadState>) =>
    setUploads((prev) => prev.map((u) => (u.name === name ? { ...u, ...patch } : u)));

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length || !accountId) return;
      const list = Array.from(files);
      // A 3D model: the whole selection is one model, in one upload.
      if (isModelSelection(list)) {
        const entry = list.find((f) => /\.(gltf|glb)$/i.test(f.name))!;
        const name = list.length > 1 ? `${entry.name} + ${list.length - 1} files` : entry.name;
        setUploads([{ name, status: 'uploading', message: '3D model' }]);
        try {
          const form = new FormData();
          form.append('accountId', accountId);
          for (const f of list) form.append('file', f, f.name);
          const res = await apiFetch('/api/upload/model', { method: 'POST', body: form });
          const out = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(out.error || `Upload failed (${res.status})`);
          const leftOut: string[] = out.leftOut || [];
          setUpload(name, {
            status: out.deduped ? 'deduped' : 'done',
            message: out.deduped
              ? 'already in your library — nothing uploaded'
              : `${out.files} files${leftOut.length ? ` · left out (not used by the model): ${leftOut.join(', ')}` : ''}`,
          });
          await refetch();
          void refetchAllowance();
        } catch (err) {
          setUpload(name, {
            status: 'error',
            message: err instanceof Error ? err.message : 'Upload failed',
          });
        }
        return;
      }
      setUploads(list.map((f) => ({ name: f.name, status: 'hashing' as const })));

      for (const file of list) {
        if (isVideoFile(file)) {
          // Straight to video hosting, in resumable pieces; encoded after.
          try {
            setUpload(file.name, { status: 'uploading', message: '0%' });
            const { assetId } = await uploadVideo(client, accountId, file, (p) =>
              setUpload(file.name, { message: `${p}%` })
            );
            setUpload(file.name, {
              status: 'done',
              message: 'processing — ready in a few minutes',
            });
            void refetch();
            void waitForVideo(client, accountId, assetId)
              .then(() => setUpload(file.name, { status: 'done', message: 'ready' }))
              .catch((err) =>
                setUpload(file.name, { status: 'error', message: (err as Error).message })
              )
              .finally(() => {
                void refetch();
                void refetchAllowance();
              });
          } catch (err) {
            setUpload(file.name, {
              status: 'error',
              message: err instanceof Error ? err.message : 'Upload failed',
            });
          }
          continue;
        }
        try {
          const sha256 = await sha256Hex(file);
          setUpload(file.name, { status: 'uploading' });

          const result = await uploadAsset(accountId, file, {
            sha256,
            filename: file.name,
          });

          setUpload(
            file.name,
            result.deduped
              ? { status: 'deduped', message: 'already in your library — nothing uploaded' }
              : { status: 'done' }
          );
        } catch (err) {
          setUpload(file.name, {
            status: 'error',
            message: err instanceof Error ? err.message : 'Upload failed',
          });
        }
      }
      await refetch();
      void refetchAllowance();
    },
    [accountId, client, refetch, refetchAllowance]
  );

  // Delete (App Prototype, Media; backlog c78d142b): say what changes on the
  // site, then go. True when it was deleted, so the versions dialog can close.
  const handleDelete = async (asset: MediaAsset): Promise<boolean> => {
    if (!accountId) return false;
    const label = asset.originalFilename || 'this file';
    const blocked = deleteBlock(asset.usage);
    if (blocked) {
      showInkToast(blocked);
      return false;
    }
    const others = (asset.usage || []).filter((u) => u.ownerType !== 'site');
    const owners = new Set(others.map((u) => `${u.ownerType}:${u.ownerId}`)).size;
    const versions = editVersions(asset.renditions).length;
    const ok = await askInk({
      title: `Delete ${label}?`,
      body:
        (others.length
          ? `It’s used in ${usedWhere(others)}. ${
              owners > 1 ? 'They' : 'It'
            } will show no image there until you choose another. `
          : 'It isn’t used anywhere, so nothing on the site changes. ') +
        (versions === 2
          ? 'Both versions are deleted, including the original.'
          : versions > 2
            ? `All ${versions} versions are deleted, including the original.`
            : 'The original is deleted.'),
      confirmLabel: others.length ? 'Delete anyway' : 'Delete',
      danger: true,
    });
    if (!ok) return false;
    try {
      await deleteAsset({ variables: { accountId, assetId: asset.id } });
    } catch (err) {
      // e.g. a video still on a page: the server says which page.
      showInkToast(err instanceof Error ? err.message : 'Couldn’t delete it');
      return false;
    }
    showInkToast(`${label} deleted.`);
    await refetch();
    void refetchAllowance();
    return true;
  };

  if (accountsLoading) return <Loader label="Loading" className="p-6 text-cmp-ink-3" />;

  const library = (
    <div
      className={
        embedded ? 'flex flex-col flex-1' : 'flex-1 min-h-0 overflow-y-auto space-y-6 pr-1'
      }
    >
      {/* Content → Media (Prototype v15): what the plan allows first, then the filters. */}
      {embedded ? <MediaAllowanceBar allowance={allowance} /> : null}
      {embedded ? (
        <div className="ink-lib-filters" style={{ flex: 'none' }}>
          <input
            className="ink-search ink-search-files"
            value={fileQuery}
            onChange={(e) => setFileQuery(e.target.value)}
            placeholder="Search media"
            aria-label="Search media"
          />
          {(
            [
              ['all', 'All', allCount],
              ['used', 'In use', usedCount],
              ['unused', 'Unused', unusedCount],
            ] as const
          ).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              className="ink-chip"
              data-active={usage === id ? 'true' : 'false'}
              onClick={() => {
                setUsage(id);
                setOffset(0);
              }}
            >
              {label} <span>{count}</span>
            </button>
          ))}
          <span className="ink-lib-filters-note">
            Originals are always kept. Crops and resizes are saved as new versions.
          </span>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      ) : (
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-cmp-ink flex items-center gap-2">
              <Images className="w-6 h-6" /> Media
            </h1>
            <p className="text-sm text-cmp-ink-2 mt-1">
              Every original is kept. Crops and resizes are new versions, so an edit never destroys
              the scan underneath it.
            </p>
          </div>
          <div>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                void handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <Button onClick={() => fileInput.current?.click()} className="gap-2">
              <Upload className="w-4 h-4" /> Upload images, audio, videos or 3D models
            </Button>
          </div>
        </header>
      )}

      <div className={embedded ? 'flex-1' : 'contents'}>
        {embedded ? null : <MediaAllowanceBar allowance={allowance} />}
        {uploads.length > 0 && (
          <Card className="p-4 space-y-2 bg-cmp-raise border border-cmp-line text-cmp-ink">
            {uploads.map((u) => (
              <div key={u.name} className="flex items-center gap-3 text-sm">
                <span className="text-cmp-ink truncate max-w-xs">{u.name}</span>
                {u.status === 'hashing' && <Badge variant="outline">hashing…</Badge>}
                {u.status === 'uploading' && <Badge variant="outline">uploading…</Badge>}
                {u.status === 'done' && <Badge>added</Badge>}
                {u.status === 'deduped' && <Badge variant="outline">already had it</Badge>}
                {u.status === 'error' && <Badge variant="destructive">failed</Badge>}
                {u.message && <span className="text-cmp-ink-3">{u.message}</span>}
              </div>
            ))}
            <button
              className="text-xs text-cmp-ink-2 hover:text-cmp-ink underline"
              onClick={() => setUploads([])}
            >
              clear
            </button>
          </Card>
        )}

        {error && (
          <Card className="p-4 text-sm text-red-400 bg-cmp-raise border border-cmp-line text-cmp-ink">
            Could not load media: {error.message}
          </Card>
        )}

        {/* Search with no matches (backlog 33da36e4): say so, with the way back. */}
        {embedded && search && page && assets.length === 0 && linked.settled && (
          <div className="ink-lib-nomatch" role="status">
            <span>
              {usage !== 'all' && allCount > 0
                ? `No ${usage === 'used' ? 'media in use' : 'unused media'} matches “${search}”.`
                : linked.total
                  ? `Nothing in your library matches “${search}”. Matches linked from your feeds are below.`
                  : `No media matches “${search}”.`}
            </span>
            <button type="button" className="ink-bill-link" onClick={clearSearch}>
              Clear search
            </button>
          </div>
        )}

        {/* An empty In use / Unused filter on a library that has files (backlog c7534c7e). */}
        {embedded && !search && page && assets.length === 0 && usage !== 'all' && allCount > 0 && (
          <div className="ink-lib-nomatch" role="status">
            <span>
              {usage === 'unused'
                ? 'Every uploaded file is in use.'
                : 'None of your uploaded files are in use yet.'}
            </span>
            <button
              type="button"
              className="ink-bill-link"
              onClick={() => {
                setUsage('all');
                setOffset(0);
              }}
            >
              Show all
            </button>
          </div>
        )}

        {!loading && !search && assets.length === 0 && (usage === 'all' || allCount === 0) && (
          <Card className="p-10 text-center text-cmp-ink-2 bg-cmp-raise border border-cmp-line text-cmp-ink">
            <Images className="w-[2rem] h-[2rem] mx-auto mb-3 opacity-50" />
            <p>
              {embedded && linked.total
                ? 'Nothing uploaded yet. The images your articles show from their feeds are listed below.'
                : 'No media yet. Upload an image, a sound file or a video, or capture one from the studio app.'}
            </p>
          </Card>
        )}

        <div
          className={
            embedded ? 'ink-media-grid' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'
          }
        >
          {shown.map((asset) => {
            const preview = displayRendition(asset);
            const versions = asset.renditions?.length ?? 0;
            const width = asset.width || preview?.width;
            const height = asset.height || preview?.height;
            const meta = [formatBytes(asset.bytes), width && height ? `${width}×${height}` : null]
              .filter(Boolean)
              .join(' · ');
            const used = asset.usageCount > 0;
            const chips = siteUses(asset.usage);
            const others = (asset.usage || []).filter((u) => u.ownerType !== 'site');
            if (embedded) {
              // App Prototype, Media (backlog c78d142b): the tile opens the
              // versions dialog; versions and delete show on hover or focus.
              const name = asset.originalFilename || 'this file';
              const vCount = editVersions(asset.renditions).length;
              const canDelete = !deleteBlock(asset.usage);
              return (
                <div key={asset.id} className="ink-media-card">
                  <div
                    className="ink-media-thumb"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${name}`}
                    onClick={() => setHistoryFor(asset)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setHistoryFor(asset);
                      }
                    }}
                  >
                    {asset.video ? (
                      <VideoThumb asset={asset} />
                    ) : isFont(asset) ? (
                      <FontThumb asset={asset} url={preview?.url} />
                    ) : isAudio(asset) ? (
                      <AudioThumb asset={asset} url={preview?.url} />
                    ) : isModel(asset) ? (
                      <ModelThumb asset={asset} url={preview?.url} />
                    ) : preview?.url ? (
                      <img src={preview.url} alt={name} loading="lazy" />
                    ) : (
                      <span className="ink-media-fallback">card scan</span>
                    )}
                    {chips.length ? (
                      <span className="ink-media-role">{chips.join(' · ')}</span>
                    ) : null}
                    {vCount > 1 && !asset.video ? (
                      <span className="ink-media-hov ink-media-vers">
                        <span aria-hidden>⟲</span>
                        {versionsLabel(vCount)}
                      </span>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        className="ink-media-hov ink-media-del"
                        aria-label={`Delete ${name}`}
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDelete(asset);
                        }}
                      >
                        <Trash2 className="w-[15px] h-[15px]" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                  <div className="ink-media-name" title={asset.originalFilename || ''}>
                    {asset.originalFilename || asset.sha256.slice(0, 10)}
                  </div>
                  <div className="ink-media-meta">
                    <span>{meta}</span>
                    <span
                      className="ink-media-used"
                      style={{ color: used ? 'var(--ink-ink3)' : 'var(--ink-amber)' }}
                      title={used ? usageSummary(asset.usage) : 'Nothing references this image.'}
                    >
                      {others.length
                        ? usedLabel(others)
                        : chips.length
                          ? 'Used site-wide'
                          : used
                            ? 'In use'
                            : 'Unused'}
                    </span>
                  </div>
                </div>
              );
            }
            return (
              <Card
                key={asset.id}
                className="overflow-hidden group bg-cmp-raise border border-cmp-line text-cmp-ink"
              >
                <div className="aspect-[3/4] bg-cmp-surface flex items-center justify-center overflow-hidden relative">
                  {asset.video ? (
                    <VideoThumb asset={asset} className="w-full h-full object-contain" />
                  ) : isFont(asset) ? (
                    <FontThumb asset={asset} url={preview?.url} />
                  ) : isModel(asset) ? (
                    <ModelThumb asset={asset} url={preview?.url} />
                  ) : preview?.url ? (
                    <img
                      src={preview.url}
                      alt={asset.originalFilename || 'media'}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Images className="w-6 h-6 opacity-40" />
                  )}
                </div>
                <div className="p-2 space-y-1">
                  <div
                    className="text-xs text-cmp-ink truncate"
                    title={asset.originalFilename || ''}
                  >
                    {asset.originalFilename || asset.sha256.slice(0, 10)}
                  </div>
                  <div className="text-[11px] text-cmp-ink-2 flex items-center gap-2">
                    <span>{formatBytes(asset.bytes)}</span>
                    {preview?.width && preview?.height && (
                      <span>
                        {preview.width}×{preview.height}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {asset.usageCount > 0 ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] gap-1"
                        title={usageSummary(asset.usage)}
                      >
                        <LinkIcon className="w-2.5 h-2.5" />
                        Used {asset.usageCount > 1 ? `${asset.usageCount}×` : ''}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-[10px] opacity-60"
                        title="Nothing references this image — safe to remove."
                      >
                        Unused
                      </Badge>
                    )}
                    {chips.map((c) => (
                      <Badge key={c} variant="outline" className="text-[10px]">
                        {c}
                      </Badge>
                    ))}
                    {asset.usageCount === 1 && !chips.length && asset.usage[0]?.title && (
                      <span
                        className="text-[10px] text-cmp-ink-3 truncate max-w-[9rem]"
                        title={asset.usage[0].title}
                      >
                        {asset.usage[0].title}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {asset.video?.status === 'ready' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 gap-1 text-[11px]"
                        onClick={() => setCoverFor(asset)}
                        title="Choose cover"
                      >
                        <ImageIcon className="w-3 h-3" /> Cover
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 gap-1 text-[11px]"
                      onClick={() => setHistoryFor(asset)}
                      title="Version history"
                    >
                      <History className="w-3 h-3" /> {versionsLabel(versions)}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => void handleDelete(asset)}
                      title="Remove from library"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* End of uploads (App Prototype): "That's all 15 uploads". */}
        {embedded && shown.length > 0 && (!page || offset + PAGE_SIZE >= page.total) && (
          <div className="ink-media-end" data-screen-label="Media end of uploads">
            <span />
            {`That’s all ${(page?.total ?? shown.length).toLocaleString()}${
              search ? ` matching “${search}”` : ''
            }${usage === 'used' ? ' in use' : usage === 'unused' ? ' unused' : ' uploads'}`}
            <span />
          </div>
        )}

        {page && page.total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-cmp-ink-2">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, page.total)} of {page.total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= page.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {embedded && accountId ? <LinkedMedia accountId={accountId} search={search} /> : null}

        {coverFor?.video && accountId && (
          <VideoCoverDialog
            accountId={accountId}
            assetId={coverFor.id}
            name={coverFor.originalFilename || 'this video'}
            video={coverFor.video}
            images={assets
              .filter((a) => !a.video && (a.mime || '').startsWith('image/'))
              .map((a) => ({
                id: a.id,
                name: a.originalFilename || 'image',
                url: displayRendition(a)?.url || '',
              }))
              .filter((a) => a.url)}
            onClose={() => setCoverFor(null)}
            onSaved={() => void refetch()}
          />
        )}

        {historyFor && (
          <VersionHistory
            asset={historyFor}
            accountId={accountId!}
            onClose={() => setHistoryFor(null)}
            onRestored={() => void refetch()}
            onDelete={async () => {
              if (await handleDelete(historyFor)) setHistoryFor(null);
            }}
            onCover={
              historyFor.video?.status === 'ready'
                ? () => {
                    setCoverFor(historyFor);
                    setHistoryFor(null);
                  }
                : undefined
            }
          />
        )}
      </div>
    </div>
  );

  if (embedded) return library;

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="m-0 w-full h-full relative overflow-hidden box-border bg-primary-background flex flex-col">
            <div className="ink-frame">{library}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** "In the text", "Lead image", "Site-wide": where a use sits, for the Used by list. */
function whereLabel(u: MediaUsage): string {
  if (u.ownerType === 'site') return 'Site-wide';
  const role = (u.role || '').trim();
  if (!role) return OWNER_LABEL[u.ownerType] || 'Used';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * The versions dialog (App Prototype, Media; backlog c78d142b): where the file
 * is used, every version of it with the one the site shows, and Delete / Copy
 * address / Done.
 *
 * Restoring is a pointer swap, not a copy — every version's bytes are still in
 * storage — so reverting a bad crop is instant and cannot half-fail.
 */
function VersionHistory({
  asset,
  accountId,
  onClose,
  onRestored,
  onDelete,
  onCover,
}: {
  asset: MediaAsset;
  accountId: string;
  onClose: () => void;
  onRestored: () => void;
  onDelete: () => void;
  /** A ready video: choose its cover. */
  onCover?: () => void;
}) {
  const { data, loading, refetch } = useQuery(GET_MEDIA_RENDITIONS, {
    variables: { accountId, assetId: asset.id },
    fetchPolicy: 'network-only',
  });
  const [restore, { loading: restoring }] = useMutation(RESTORE_MEDIA_RENDITION);
  const renditions = editVersions(data?.mediaRenditions ?? []);
  const served = servedId(renditions);
  const servedUrl = renditions.find((r) => r.id === served)?.url || null;
  const blocked = deleteBlock(asset.usage);
  const name = asset.originalFilename || 'Image';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleRestore = async (renditionId: string) => {
    await restore({ variables: { accountId, renditionId } });
    await refetch();
    onRestored();
  };

  const copyAddress = () => {
    if (!servedUrl) return;
    void navigator.clipboard
      ?.writeText(servedUrl)
      .then(() => showInkToast('Image address copied.'))
      .catch(() => showInkToast('Couldn’t copy the address.'));
  };

  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ink-modal ink-media-dlg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-dlg-name"
        data-screen-label="Media versions"
      >
        <div className="ink-media-dlg-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ink-media-dlg-name" id="media-dlg-name">
              {name}
            </div>
            <div className="ink-media-dlg-sub">
              Where it is used, and every version of it. Originals are always kept.
            </div>
          </div>
          <button type="button" className="ink-media-dlg-x" aria-label="Close" onClick={onClose}>
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>

        <div className="ink-media-dlg-body">
          <div className="ink-media-dlg-sec">
            <div className="ink-media-dlg-label">Used by</div>
            <div className="ink-media-dlg-box">
              {asset.usage.map((u) => {
                const href = usageHref(u);
                const title =
                  u.ownerType === 'site'
                    ? `${u.title || u.role} in Website › Manage › Site`
                    : u.title || 'Untitled';
                return (
                  <div
                    key={`${u.ownerType}-${u.ownerId}-${u.role}-${u.position}`}
                    className="ink-media-dlg-use"
                  >
                    <span className="ink-media-dlg-where">{whereLabel(u)}</span>
                    {href ? (
                      <Link to={href} className="ink-media-dlg-title" onClick={onClose}>
                        {title}
                      </Link>
                    ) : (
                      <span className="ink-media-dlg-title" data-plain="true">
                        {title}
                      </span>
                    )}
                    <span />
                    <span className="ink-media-dlg-meta">
                      {u.ownerType === 'site' ? 'Shown on every page' : usageKind(u)}
                    </span>
                  </div>
                );
              })}
              {asset.usage.length === 0 ? (
                <div className="ink-media-dlg-empty">
                  Not used anywhere yet. Deleting it changes nothing on the site.
                </div>
              ) : null}
            </div>
            {blocked ? <div className="ink-media-dlg-note">{blocked}</div> : null}
          </div>

          <div className="ink-media-dlg-sec">
            <div className="ink-media-dlg-label">
              Versions <span className="ink-media-dlg-count">{renditions.length || ''}</span>
            </div>
            <div className="ink-media-dlg-box">
              {loading && !renditions.length ? (
                <Loader label="Loading versions" className="ink-media-dlg-empty" />
              ) : null}
              {renditions.map((r) => (
                <div key={r.id} className="ink-media-dlg-ver">
                  <span className="ink-media-dlg-vlabel">{KIND_LABEL[r.kind] || r.kind}</span>
                  <span className="ink-media-dlg-dims">
                    {r.width && r.height ? `${r.width}×${r.height} · ` : ''}
                    {formatBytes(r.bytes)}
                  </span>
                  {r.id === served ? <span className="ink-media-dlg-live">On the site</span> : null}
                  {!r.isCurrent && r.kind !== 'original' ? (
                    <button
                      type="button"
                      className="ink-quiet-link"
                      disabled={restoring}
                      onClick={() => void handleRestore(r.id)}
                    >
                      Restore
                    </button>
                  ) : null}
                  <span className="ink-media-dlg-date">{whenLabel(r.createdAt)}</span>
                </div>
              ))}
              {!loading && renditions.length === 0 ? (
                <div className="ink-media-dlg-empty">No versions recorded yet.</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="ink-media-dlg-foot">
          {blocked ? null : (
            <button type="button" className="ink-media-dlg-del" onClick={onDelete}>
              Delete…
            </button>
          )}
          <span style={{ marginLeft: 'auto' }} />
          {onCover ? (
            <button type="button" className="ink-btn" onClick={onCover}>
              Choose cover
            </button>
          ) : null}
          {servedUrl ? (
            <button type="button" className="ink-btn" onClick={copyAddress}>
              Copy address
            </button>
          ) : null}
          <button type="button" className="ink-btn ink-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
